import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import {
  sendPasswordPgw,
  loginToken,
  setUserEntry,
  sendPasswordVerify,
  confirmPasswordReset,
} from "../../server/Customer";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import Input from "../containers/InputForm";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup
  .object({
    userName: yup.string().required("שדה חובה").trim(),
    password: yup
      .string()
      .required("חובה להכניס סיסמה")
      .matches(/[a-zA-Z]/, "חייב להכיל אותיות באנגלית")
      .matches(/[0-9.,-]/, "חייב להכיל לפחות מספר או תו מיוחד")
      .min(8, "לפחות 8 תווים")
      .max(72, "מקסימום 72 תווים"),
  })
  .required();

const resetSchema = yup.object({
  newPassword: yup
    .string()
    .required("חובה להכניס סיסמה")
    .matches(/[a-zA-Z]/, "חייב להכיל אותיות באנגלית")
    .matches(/[0-9.,-]/, "חייב להכיל לפחות מספר או תו מיוחד")
    .min(8, "לפחות 8 תווים")
    .max(72, "מקסימום 72 תווים"),
  confirmPassword: yup
    .string()
    .required("חובה לאשר סיסמה")
    .oneOf([yup.ref("newPassword")], "הסיסמאות לא תואמות"),
});

const UserLogin = ({
  onInlineError,
  onClearInlineError,
  onSwitchToRegister,
}) => {
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors },
  } = useForm({
    resolver: yupResolver(resetSchema),
  });

  const [email, setEmail] = useState("");
  const [forgotPass, setForgotPass] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [userId, setUserId] = useState(null);
  const [maxAttempts, setMaxAttempts] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [validationCode, setValidationCode] = useState("");
  const [isVerified, setIsVerified] = useState(false);

  const sendPass = () => {
    onClearInlineError?.();
    sendPasswordPgw(email.trim().toLowerCase(), { suppressAlert: true })
      .then((res) => {
        setUserId(res.userId);
        setMaxAttempts(res.maxAttempts);
      })
      .catch(() =>
        onInlineError?.("לא הצלחנו לשלוח קוד איפוס. נסה שוב.", "שגיאה"),
      );
  };

  const verify = () => {
    if (attempts <= maxAttempts) {
      onClearInlineError?.();
      sendPasswordVerify(validationCode, userId, { suppressAlert: true })
        .then(() => {
          setIsVerified(true);
        })
        .catch(() => onInlineError?.("קוד האימות אינו תקין.", "שגיאה"));

      setAttempts((prev) => prev + 1);
    } else {
      onInlineError?.("ניסית יותר מדי פעמים. נסה שוב מאוחר יותר.", "שגיאה");
    }
  };

  const confirmPass = (data) => {
    onClearInlineError?.();
    confirmPasswordReset(userId, validationCode, data.newPassword, {
      suppressAlert: true,
    })
      .then(() => {
        setForgotPass(false);
        setIsVerified(false);
      })
      .catch(() => onInlineError?.("משהו השתבש. נסה שוב.", "שגיאה"));
  };

  const signIn = async (data) => {
    onClearInlineError?.();
    data.userName = data.userName.trim().toLowerCase();
    data["grant_type"] = "password";
    try {
      await dispatch(loginToken(data, true, { suppressAlert: true }));
    } catch (e) {
      onInlineError?.(
        e?.message || "שם משתמש וסיסמה שגויים",
        "פרטי ההתחברות לא תקינים",
      );
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassVisibility = () => {
    setShowConfirmPass(!showConfirmPass);
  };

  return !forgotPass ? (
    <form className="login-form" onSubmit={handleSubmit(signIn)}>
      <div className="hz-login-field hz-login-field--email">
        <Input
          errors={errors}
          label="דואר אלקטרוני"
          type="text"
          name="userName"
          register={register}
        />
      </div>

      <div className="hz-login-field hz-login-field--password">
        <div className="hz-login-password">
          <div className="input-wrapper">
            <input
              className="hz-login-password__input"
              type={showPassword ? "text" : "password"}
              {...register("password")}
              id="password"
              aria-label="password"
              autoComplete="current-password"
            />
            <button
              type="button"
              className="hz-login-password__toggle"
              onClick={togglePasswordVisibility}
              aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
            >
              {showPassword ? (
                <EyeOff size={18} strokeWidth={2} aria-hidden />
              ) : (
                <Eye size={18} strokeWidth={2} aria-hidden />
              )}
            </button>
          </div>

          {errors?.password ? (
            <div className="text-danger">{errors.password.message}</div>
          ) : null}
        </div>
      </div>

      <button className="login-btn" type="submit">
        כניסה &gt;
      </button>

      <span onClick={() => setForgotPass(true)} className="forgot-pass">
        שכחת את הסיסמה?
      </span>

      <button
        className="cancel"
        onClick={() => dispatch(setUserEntry([false, "login"]))}
      >
        כניסה ללא הרשמה&gt;
      </button>
    </form>
  ) : (
    <div className="forgot-pass-wrap">
      {!userId ? (
        <>
          <p>אנא הקלד את כתובת המייל לאיפוס סיסמה</p>
          <input
            type="text"
            onChange={(e) => setEmail(e.target.value)}
            placeholder="אימייל"
            value={email}
          />
          <button className="send-btn" onClick={sendPass}>
            שלח
          </button>
        </>
      ) : !isVerified ? (
        <>
          <p>אנא הקלד את הקוד שקיבלת</p>
          <input
            type="text"
            onChange={(e) => setValidationCode(e.target.value)}
            placeholder="קוד אימות"
            value={validationCode}
          />
          <button className="send-btn" onClick={verify}>
            שלח
          </button>
        </>
      ) : (
        <form onSubmit={handleSubmitReset(confirmPass)}>
          <Input
            type={showPassword ? "text" : "password"}
            name="newPassword"
            label="סיסמה חדשה"
            className="password-input"
            toggleVisibility={togglePasswordVisibility}
            register={registerReset}
            errors={resetErrors}
            showPassword={showPassword}
            isPasswordField
          />

          <Input
            type={showConfirmPass ? "text" : "password"}
            name="confirmPassword"
            label="אימות סיסמה"
            toggleVisibility={toggleShowConfirmPassVisibility}
            register={registerReset}
            errors={resetErrors}
            showPassword={showConfirmPass}
            isPasswordField
          />

          <button className="send-btn" type="submit">
            שלח
          </button>
        </form>
      )}
    </div>
  );
};

export default UserLogin;
